import React, { useState } from 'react';
import { pb } from '@/lib/pocketbase';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SecureHtml } from '@/components/ui/secure-html';
import { toast } from 'sonner';
import { CheckCircle, FileText, Shield, Calendar } from 'lucide-react';
import { PendingAgreement } from './UnifiedConsentGuard';

interface UnifiedConsentModalProps {
  open: boolean;
  onConsent: () => void;
  onCancel: () => void;
  userId: string;
  userType: 'tutor' | 'student' | 'admin';
  pendingAgreements: PendingAgreement[];
}

// Helper component to securely render terms content
const SecureTermsHtml = ({ content }: { content: string }) => {
  return <SecureHtml content={content} className="prose max-w-none text-sm" />;
};

export const UnifiedConsentModal = ({
  open,
  onConsent,
  onCancel,
  userId,
  userType,
  pendingAgreements
}: UnifiedConsentModalProps) => {
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getUserIP = async (): Promise<string> => {
    const services = [
      'https://api.ipify.org?format=json',
      'https://api64.ipify.org?format=json',
      'https://ipapi.co/json/',
      'https://api.my-ip.io/ip.json'
    ];

    for (const service of services) {
      try {
        console.log(`Trying IP service: ${service}`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const response = await fetch(service, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (!response.ok) {
          console.warn(`IP service ${service} returned status ${response.status}`);
          continue;
        }
        
        const data = await response.json();
        let ip = null;
        
        // Different services return IP in different formats
        if (data.ip) {
          ip = data.ip;
        } else if (data.query) {
          ip = data.query;
        } else if (typeof data === 'string') {
          ip = data;
        }
        
        if (ip && ip !== 'unknown') {
          console.log(`Successfully retrieved IP: ${ip} from ${service}`);
          return ip;
        }
      } catch (error) {
        console.warn(`Error with IP service ${service}:`, error);
        continue;
      }
    }
    
    // Fallback: try to get WebRTC IP (local network IP)
    try {
      const rtcIp = await getWebRTCIP();
      if (rtcIp && rtcIp !== 'unknown') {
        console.log(`Using WebRTC IP as fallback: ${rtcIp}`);
        return rtcIp;
      }
    } catch (error) {
      console.warn('WebRTC IP detection failed:', error);
    }
    
    console.error('All IP detection methods failed, using unknown');
    return 'unknown';
  };

  const getWebRTCIP = (): Promise<string> => {
    return new Promise((resolve) => {
      const rtc = new RTCPeerConnection({ iceServers: [] });
      let ip = 'unknown';
      
      rtc.createDataChannel('');
      rtc.createOffer().then(offer => rtc.setLocalDescription(offer));
      
      rtc.onicecandidate = (ice) => {
        if (!ice || !ice.candidate || !ice.candidate.candidate) return;
        
        const candidate = ice.candidate.candidate;
        const ipMatch = candidate.match(/([0-9]{1,3}\.){3}[0-9]{1,3}/);
        if (ipMatch && ipMatch[0] !== '0.0.0.0') {
          ip = ipMatch[0];
          rtc.close();
          resolve(ip);
        }
      };
      
      // Timeout after 3 seconds
      setTimeout(() => {
        rtc.close();
        resolve(ip);
      }, 3000);
    });
  };

  const handleSubmit = async () => {
    if (!hasAgreed || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const userIP = await getUserIP();
      const timestamp = new Date().toISOString();

      // Create agreements for all pending terms
      for (const agreement of pendingAgreements) {
        console.log('Creating agreement for:', agreement.type, 'User ID:', userId, 'User Type:', userType);
        
        if (agreement.type === 'universal_terms') {
          // Create universal agreement
          await pb.collection('user_agreements').create({
            user_id: userId,
            terms_version: agreement.version,
            agreement_type: 'universal_terms',
            agreed_at: timestamp,
            ip_address: userIP,
            user_agent: navigator.userAgent,
            user_type: userType,
            is_active: true
          });
          console.log('Universal agreement created successfully');
        } else if (agreement.type === 'tutor_terms') {
          // Create tutor agreement
          await pb.collection('tutor_agreements').create({
            tutor_id: userId,
            terms_version: agreement.version,
            agreement_type: 'initial_signup',
            agreed_at: timestamp,
            ip_address: userIP,
            user_agent: navigator.userAgent,
            terms_content: agreement.content,
            is_active: true
          });
        }
      }

      
      onConsent();
    } catch (error) {
      console.error('Error submitting agreements:', error);
      toast.error('Failed to submit agreements. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAgreementIcon = (type: string) => {
    switch (type) {
      case 'universal_terms':
        return <Shield className="h-4 w-4" />;
      case 'tutor_terms':
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getAgreementTitle = (type: string) => {
    switch (type) {
      case 'universal_terms':
        return 'Universal Platform Terms';
      case 'tutor_terms':
        return 'Tutor-Specific Terms';
      default:
        return 'Terms & Conditions';
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b flex-shrink-0">
          <DialogTitle className="text-2xl font-bold">
            Terms & Conditions Agreement
          </DialogTitle>
          <DialogDescription className="text-base">
            Please review and accept the following terms to continue using our platform.
            You have {pendingAgreements.length} agreement{pendingAgreements.length > 1 ? 's' : ''} to review.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full p-6">
            {/* Agreement Summary */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Agreements to Review:</h3>
              <div className="grid gap-2">
                {pendingAgreements.map((agreement, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    {getAgreementIcon(agreement.type)}
                    <div className="flex-1">
                      <div className="font-medium">{getAgreementTitle(agreement.type)}</div>
                      <div className="text-sm text-muted-foreground">
                        Version {agreement.version} • Effective {new Date(agreement.effectiveDate).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge variant="outline">{agreement.type.replace('_', ' ')}</Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Terms Content */}
            <Tabs defaultValue="0" className="w-full">
              <TabsList className="grid w-full grid-cols-auto">
                {pendingAgreements.map((agreement, index) => (
                  <TabsTrigger key={index} value={index.toString()} className="text-sm">
                    {getAgreementTitle(agreement.type)}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {pendingAgreements.map((agreement, index) => (
                <TabsContent key={index} value={index.toString()} className="mt-4">
                  <div className="border rounded-lg">
                    <div className="p-4 border-b bg-muted/30">
                      <div className="flex items-center gap-2 mb-2">
                        {getAgreementIcon(agreement.type)}
                        <h4 className="font-semibold">{agreement.title}</h4>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Version {agreement.version}
                        </div>
                        <div>
                          Effective: {new Date(agreement.effectiveDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <ScrollArea className="h-64 p-4">
                      <SecureTermsHtml content={agreement.content} />
                    </ScrollArea>
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            <Separator className="my-6" />

            {/* Key Points Summary */}
            <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800 mb-6">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                By accepting, you acknowledge:
              </h4>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 ml-6">
                <li>• You have read and understood all terms presented</li>
                <li>• You agree to comply with platform policies and guidelines</li>
                <li>• You understand your rights and responsibilities as a {userType}</li>
                <li>• These agreements may be updated and you'll be notified of changes</li>
              </ul>
            </div>
          </ScrollArea>
        </div>

        {/* Fixed Bottom Section */}
        <div className="border-t p-6 bg-background flex-shrink-0">
          {/* Agreement Checkbox */}
          <div className="flex items-start space-x-3 mb-4">
            <Checkbox
              id="agree-terms"
              checked={hasAgreed}
              onCheckedChange={(checked) => setHasAgreed(checked as boolean)}
              className="mt-0.5"
            />
            <label
              htmlFor="agree-terms"
              className="text-sm font-medium leading-relaxed cursor-pointer"
            >
              I have read, understood, and agree to be bound by all the terms and conditions presented above.
              I acknowledge that these agreements are legally binding and that I am accepting them voluntarily.
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel Registration
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!hasAgreed || isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Processing...
                </>
              ) : (
                'Accept All & Continue'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};